import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BillingPlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
}

export default function BillingPlansModal({
  isOpen,
  onClose,
  onSubscribe,
}: BillingPlansModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Choose your plan
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </Button>
        </div>

        {/* Plans */}
        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <div className="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 flex flex-col">
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-gray-100">
                Free
              </h3>
              <div className="text-3xl font-extrabold mb-3 text-gray-900 dark:text-gray-100">
                $0
                <span className="text-base text-slate-600 dark:text-slate-400 font-normal">
                  /forever
                </span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Perfect for trying out Infralyst
              </p>
            </div>

            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>3 free PRs (lifetime)</strong>
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Unlimited savings recommendations
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Connect unlimited AWS + GitHub + Terraform states
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Slack notifications
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Invite teammates
                </span>
              </li>
            </ul>

            <Button variant="surface" disabled className="w-full">
              Current plan
            </Button>
          </div>

          {/* Infralyst Pro Plan */}
          <div className="rounded-xl border-2 border-primary bg-white dark:bg-slate-900 p-6 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="inline-block rounded-full bg-primary text-white px-4 py-1 text-xs font-semibold">
                Best value
              </span>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-gray-100">
                Infralyst Pro
              </h3>
              <div className="text-3xl font-extrabold mb-3 text-gray-900 dark:text-gray-100">
                $99
                <span className="text-base text-slate-600 dark:text-slate-400 font-normal">
                  /month
                </span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Unlimited PR generations for growing teams
              </p>
            </div>

            <ul className="space-y-3 mb-6 flex-1">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Unlimited PRs</strong>
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Everything in Free, plus:
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Priority support
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Early-adopter price lock
                </span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  30-day money-back guarantee
                </span>
              </li>
            </ul>

            <Button onClick={onSubscribe} className="w-full">
              Subscribe to Infralyst Pro
            </Button>

            </div>
        </div>

        <p className="px-6 pb-6 pt-3 text-xs text-slate-500 dark:text-slate-400 text-center">
          Business billing only. Prices exclude tax. EU/UK customers: A valid
          VAT number is required.
        </p>
      </div>
    </div>
  );
}
